import { DecimalPipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, map, pairwise, startWith, takeUntil } from 'rxjs';
import { ClaimForm, ClaimsForm } from 'src/app/services/form/form.controls';
import {
  CLAIMS_FORM_NAME,
  DEBTOR_FORM_NAME,
  FormFactory,
} from 'src/app/services/form/form.factory';
import { FormService } from 'src/app/services/form/form.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { ClaimFormComponent } from './claim-form/claim-form.component';

@Component({
  selector: 'app-claims-form',
  templateUrl: './claims-form.component.html',
  styleUrls: ['./claims-form.component.scss'],
  imports: [
    ClaimFormComponent,
    PayeeButtonComponent,
    PayeeIconComponent,
    DecimalPipe,
    TranslateModule,
  ],
  host: {
    '[class.hide]': 'hide()',
  }
})
export class ClaimsFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private readonly destroy = new Subject<void>();

  get form(): ClaimsForm {
    return this.formService.form.controls[CLAIMS_FORM_NAME]!;
  }

  private readonly debtorForm = this.formService.form.controls[DEBTOR_FORM_NAME];
  private readonly debtorFormStatus = this.debtorForm ? toSignal(this.debtorForm.statusChanges) : null;
  private readonly claimsFormValue = toSignal(
    this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map(() => this.form.getRawValue()),
    ),
  );
  readonly hide = computed((): boolean => {
    const hasFilledClaim = (this.claimsFormValue() ?? []).some((claim) => (
      !!claim.file || !!claim.original_amount || !!claim.due_date_at
    ));
    if (hasFilledClaim) {
      return false;
    }

    if (this.debtorFormStatus?.() === 'INVALID') {
      return true;
    }

    return !this.formService.debtorCollapsed();
  });

  filteredClaims: ClaimForm[] = [];
  pageIndex = 0;
  pageSize = 5;

  readonly isEditable = signal(true);

  get canAddClaim(): boolean {
    return this.form.controls.every((c) => {
      const amount = c.controls.original_amount;
      const dueDate = c.controls.due_date_at;
      return amount.value && dueDate.value;
    });
  }

  ngOnInit(): void {
    this.changePage({
      length: this.form.length,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.form.value), pairwise())
      .subscribe({
        next: ([prev, claims]) => {
          this.isEditable.set(this.form.controls.some((c) => c.value.is_editable));

          // If claim was added we have to navigate to its page
          const maxPageIndex =
            Math.max(1, Math.ceil(claims.length / this.pageSize)) - 1;
          if (prev.length < claims.length) {
            this.changePage({
              length: this.form.length,
              pageIndex: maxPageIndex,
              pageSize: this.pageSize,
            });
          } else if (prev.length > claims.length) {
            this.changePage({
              length: this.form.length,
              pageIndex:
                this.pageIndex > maxPageIndex ? maxPageIndex : this.pageIndex,
              pageSize: this.pageSize,
            });
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  addClaim(): void {
    if (this.form.valid) {
      const form = new FormFactory(this.formService, this.fb).getForm(
        'claim',
      ) as ClaimForm;

      this.form.push(form);
    } else {
      this.helpersService.markAllChildrenAsDirty(this.form);
    }
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    this.filteredClaims = this.form.controls.slice(
      event.pageIndex * event.pageSize,
      (event.pageIndex + 1) * event.pageSize,
    );
  }
}
